import { useCallback, useEffect, useRef, useState } from 'react';
import { usePrivy, useLogin } from '@privy-io/react-auth';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import { LOGIN_USER } from '../store/actions/user.actions';
import { PPAPI } from '../config/privyConfig';
import { registrationErrorMessage } from '../utils/partnerApi';
import logoutLogger from '../utils/logoutLogger';
import { ONBOARDING_PENDING_KEY } from '../components/globalView/AccountTypeOnboardingModal';

/**
 * Privy → partner session.
 *
 * The browser only ever sends ppapi the Privy access token; ppapi asks Privy
 * who that is and decides which partner account(s) it may sign in to
 * (privyLogin). Possible answers:
 *   - session payload                → signed in
 *   - 401 { twoFactorRequired }      → ask for the authenticator code
 *   - { choose: [...] }              → identity matches several partner
 *                                      accounts (never merged) → account picker
 *   - { noAccount: true }            → offer to create one (privyRegister)
 *
 * The chosen account is remembered per Privy DID so the picker shows once.
 */
const choiceKey = (did) => `privyPartnerChoice:${did}`;

const mintSession = (dispatch, data, raw) => {
    localStorage.removeItem('impersonation');
    localStorage.setItem('hash', data.hash);
    localStorage.setItem('username', data.email || '');
    localStorage.setItem('expirationTime', new Date().getTime() + 14400000);
    localStorage.setItem('affiliateId', data.affiliateId);
    sessionStorage.removeItem('manualLogout');
    dispatch({ type: LOGIN_USER, payload: raw });
};

const hasValidSession = () => {
    const hash = localStorage.getItem('hash');
    const expiry = localStorage.getItem('expirationTime');
    return Boolean(hash && expiry && Date.now() < parseInt(expiry, 10));
};

/**
 * Re-run privyLogin for the account already in session (no picker, no toast).
 * Used after the partner changes his Privy email so ppapi syncs it into the
 * partner row and the header/dropdown pick up the fresh session payload.
 */
export const refreshPartnerSession = async (dispatch, getAccessToken, did) => {
    const accessToken = await getAccessToken();
    if (!accessToken) return { ok: false };
    const payload = { privyLogin: true, accessToken, urlUsed: window.location.href };
    const remembered = did ? localStorage.getItem(choiceKey(did)) : null;
    const current = localStorage.getItem('affiliateId');
    const chosen = remembered || current;
    if (chosen) payload.partnerId = chosen;
    const res = await axios.post(PPAPI, payload);
    const data = res.data?.response;
    if (!data?.hash) return { ok: false };
    mintSession(dispatch, data, res.data);
    return { ok: true };
};

export const usePrivyAuth = () => {
    const dispatch = useDispatch();
    const { ready, authenticated, user, getAccessToken, logout } = usePrivy();
    const [isProcessing, setIsProcessing] = useState(false);
    const [chooser, setChooser] = useState(null);        // [{partnerId,name,email,wallet,via,lastLogin}]
    const [twoFactorPrompt, setTwoFactorPrompt] = useState(null); // { partnerId }
    const [noAccount, setNoAccount] = useState(null);    // { emails, wallets }
    const [error, setError] = useState('');
    const inFlightRef = useRef(false);
    const autoTriedRef = useRef(false);

    const did = user?.id || null;

    const signIn = useCallback(async ({ partnerId, twoFactorCode } = {}) => {
        if (inFlightRef.current) return { ok: false };
        inFlightRef.current = true;
        setIsProcessing(true);
        setError('');
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) {
                // Privy says authenticated but cannot mint a token (stale session: localhost, long idle,
                // app-id change). Drop it so the button turns back into "Sign in" and the next click
                // opens the Privy modal, instead of "Continue" repeating this same failure.
                try { await logout(); } catch (_) { /* nothing to drop */ }
                sessionStorage.setItem('manualLogout', 'true');
                setError('Your sign-in session expired. Please sign in again.');
                return { ok: false };
            }
            const remembered = did ? localStorage.getItem(choiceKey(did)) : null;
            const payload = { privyLogin: true, accessToken, urlUsed: window.location.href };
            const chosen = partnerId || remembered;
            if (chosen) payload.partnerId = chosen;
            if (twoFactorCode) payload.twoFactorCode = twoFactorCode;

            const res = await axios.post(PPAPI, payload);
            const data = res.data?.response;

            if (data?.hash) {
                if (did && chosen) localStorage.setItem(choiceKey(did), chosen);
                setChooser(null);
                setTwoFactorPrompt(null);
                setNoAccount(null);
                mintSession(dispatch, data, res.data);
                toast.success('Signed in successfully!');
                return { ok: true };
            }
            if (Array.isArray(data?.choose)) {
                setChooser(data.choose);
                return { ok: false, choose: true };
            }
            if (data?.noAccount) {
                setNoAccount({ emails: data.emails || [], wallets: data.wallets || [] });
                return { ok: false, noAccount: true };
            }
            setError(data?.error || 'Sign-in failed. Please try again.');
            return { ok: false };
        } catch (e) {
            const status = e?.response?.status;
            if (status === 401 && e?.response?.data?.twoFactorRequired) {
                setTwoFactorPrompt({ partnerId: e.response.data.partnerId || partnerId || null });
                return { ok: false, twoFactor: true };
            }
            if (status === 403 && did) {
                // Remembered account is no longer linked to this identity — forget it and re-resolve.
                localStorage.removeItem(choiceKey(did));
                inFlightRef.current = false;
                setIsProcessing(false);
                return signIn({ twoFactorCode });
            }
            const msg = e?.response?.data?.response?.error
                || (status === 401 ? 'Your sign-in session expired. Please sign in again.' : 'Sign-in failed. Please try again.');
            if (status === 401) {
                // ppapi could not verify the Privy token: same stale-session case as above.
                try { await logout(); } catch (_) { /* nothing to drop */ }
                sessionStorage.setItem('manualLogout', 'true');
            }
            setError(msg);
            logoutLogger.log('[privyAuth] signIn failed', { status, msg });
            return { ok: false };
        } finally {
            inFlightRef.current = false;
            setIsProcessing(false);
        }
    }, [dispatch, did, getAccessToken, logout]);

    const register = useCallback(async (companyName, turnstileToken = null) => {
        setIsProcessing(true);
        setError('');
        try {
            const accessToken = await getAccessToken();
            // Agency invite (KOL_CAMPAIGNS_AGENCY_PLAN.md §7.3 C): ppapi creates the
            // account as `kol`, already linked to the inviting agency.
            let agencyInvite = null;
            try { agencyInvite = sessionStorage.getItem('pendingAgencyInvite') || null; } catch (_) { /* private mode */ }
            const res = await axios.post(PPAPI, {
                privyRegister: true,
                accessToken,
                companyName,
                ...(turnstileToken ? { turnstileToken } : {}),
                ...(agencyInvite ? { agencyInvite } : {}),
                urlUsed: window.location.href,
            });
            const data = res.data?.response;
            if (data?.hash) {
                setNoAccount(null);
                mintSession(dispatch, data, res.data);
                if (agencyInvite && data?.agencyLinked) { try { sessionStorage.removeItem('pendingAgencyInvite'); } catch (_) { /* ignore */ } }
                // Brand-new account (not an invited KOL, which ppapi already typed): ask what the dashboard is for
                // once the 2FA notice is out of the way (AccountTypeOnboardingModal).
                if (!data?.existing && !data?.agencyLinked) localStorage.setItem(ONBOARDING_PENDING_KEY, String(data.affiliateId || '1'));
                // ppapi answers 200 + existing:true when this Privy identity already owns an account.
                toast.success(data?.existing ? 'Welcome back — signed in to your existing account.' : 'Account created — welcome aboard!');
                return { ok: true };
            }
            return { ok: false, error: registrationErrorMessage(res.data) };
        } catch (e) {
            const d = e?.response?.data?.response;
            if (d?.existing) {
                // Raced with an account that now matches — just sign in.
                setNoAccount(null);
                return signIn();
            }
            return { ok: false, error: registrationErrorMessage(e?.response?.data) };
        } finally {
            setIsProcessing(false);
        }
    }, [dispatch, getAccessToken, signIn]);

    const chooseAccount = useCallback((partnerId) => signIn({ partnerId }), [signIn]);

    const submit2FA = useCallback(async (code) => {
        const r = await signIn({ partnerId: twoFactorPrompt?.partnerId || undefined, twoFactorCode: code });
        return r.ok;
    }, [signIn, twoFactorPrompt]);

    const cancel = useCallback(() => {
        setChooser(null);
        setTwoFactorPrompt(null);
        setNoAccount(null);
        setError('');
    }, []);

    /** Forget the remembered account for this identity and show the picker again. */
    const switchAccount = useCallback(() => {
        if (did) localStorage.removeItem(choiceKey(did));
        return signIn();
    }, [did, signIn]);

    // Privy modal completed (fresh login) → resolve the partner immediately.
    const { login } = useLogin({
        onComplete: () => {
            sessionStorage.removeItem('manualLogout');
            signIn();
        },
        onError: (err) => {
            if (err && err !== 'exited_auth_flow') {
                logoutLogger.log('[privyAuth] Privy login error', { err: String(err) });
            }
        },
    });

    // Returning visitor: Privy session still alive on this origin but no CMS
    // session → sign in without a click. Skipped right after an explicit
    // logout / deploy wipe (manualLogout) so nobody is re-logged in unasked.
    useEffect(() => {
        if (!ready || !authenticated || autoTriedRef.current) return;
        if (hasValidSession()) return;
        if (sessionStorage.getItem('manualLogout') === 'true') return;
        if (localStorage.getItem('impersonation') === 'true') return;
        autoTriedRef.current = true;
        signIn();
    }, [ready, authenticated, signIn]);

    return {
        ready,
        authenticated,
        privyUser: user,
        isProcessing,
        error,
        chooser,
        twoFactorPrompt,
        noAccount,
        login,
        signIn,
        register,
        chooseAccount,
        submit2FA,
        switchAccount,
        cancel,
        privyLogout: logout,
    };
};

export default usePrivyAuth;
